import { i18n } from '@lingui/core'
import classNames from 'clsx'
import { arrayOf, bool, node, shape, string } from 'prop-types'
import { Component } from 'react'

import { IS_JEST, IS_STORYBOOK } from 'shared/consts'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import { fetchPreQuerySearchSuggestions } from 'views/components/organisms/PreQuerySuggestions/helpers'
import LoadingWrapper from 'views/components/organisms/PreQuerySuggestions/LoadingWrapper'
import TopProducts from 'views/components/organisms/PreQuerySuggestions/TopProducts'
import TopSearches from 'views/components/organisms/PreQuerySuggestions/TopSearches'
import type {
  SearchHistorySuggestion,
  SearchHit,
} from 'views/components/organisms/PreQuerySuggestions/types'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { publicRuntimeConfigType } from 'views/utils/propTypes'

import PopularCategoriesOrBrands from './PopularCategoriesOrBrands'

import './PreQuerySuggestions.scss'

class PreQuerySuggestions extends Component<ComponentProps, ComponentState> {
  state: ComponentState = this.getInitialState()

  constructor(props: ComponentProps) {
    super(props)

    if (IS_JEST || IS_STORYBOOK) {
      // Don't disturb Chromatic
      return
    }
  }

  getInitialState(): ComponentState {
    const { topProducts, topSearches } = this.props

    // Top searches & products can be passed through the props:
    // it is used only for the Storybook stories
    return {
      topSearches: Array.isArray(topSearches) ? topSearches : [],
      topProducts: Array.isArray(topProducts) ? topProducts : [],
    } as unknown as ComponentState // TS linter can't comprehend it is safe
  }

  componentDidMount() {
    if (IS_JEST || IS_STORYBOOK) {
      return
    }

    void fetchPreQuerySearchSuggestions().then(response => {
      const { topSearches, topProductsSuggestions } = response
      this.setState({ topSearches, topProducts: topProductsSuggestions })
    })
  }

  render() {
    const {
      hasSearchHistory,
      mobile,
      children,
      query,
      publicRuntimeConfig: {
        publicConfig: { suggestBrands, suggestCategories },
      },
    } = this.props

    const { topSearches, topProducts } = this.state
    const hasTopProducts = Boolean(topProducts?.length)
    const hasTopSearches = Boolean(topSearches?.length)
    const deviceClass = mobile ? 'mobile' : 'desktop'
    const topBrands = suggestBrands?.[deviceClass] ?? []
    const topCategories = suggestCategories?.[deviceClass] ?? []
    let title = i18n._('general.label.popularCategories')

    const getTopBrandsOrCategories = () => {
      if (topBrands.length > 0) {
        title = i18n._('general.label.popularBrands')
        return topBrands
      } else if (topCategories.length > 0) {
        return topCategories
      } else {
        return []
      }
    }
    const topBrandsOrCategories = getTopBrandsOrCategories()

    if ((!hasTopProducts && !hasTopSearches) || query) {
      return <LoadingWrapper mobile={mobile} />
    }

    return (
      <div
        className={classNames('o-PreQuerySuggestions__results', {
          'o-SearchBox__results--mobile': mobile,
          'o-SearchBox__results u-padding-x-small': !mobile,
        })}
      >
        <div className="l-flex l-flex--start">
          {hasSearchHistory && (
            <div className="u-padding--right@medium u-padding-small--right">
              {children}
            </div>
          )}
          {hasTopSearches && (
            <TopSearches
              hits={topSearches}
              hasSearchHistory={hasSearchHistory}
            />
          )}
          {topBrandsOrCategories?.length > 0 && (
            <PopularCategoriesOrBrands
              listItems={
                topBrandsOrCategories as { name: string; url: string }[]
              }
              title={title}
            />
          )}
        </div>
        {hasTopProducts && (
          <TopProducts hits={topProducts} mobile={Boolean(mobile)} />
        )}
      </div>
    )
  }
}

const propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  hasSearchHistory: bool,
  children: node,
  mobile: bool,
  topSearches: arrayOf(shape({})),
  topProducts: arrayOf(shape({})),
  query: string,
}

const defaultProps = {
  hasSearchHistory: false,
  children: null,
  mobile: false,
  topSearches: null,
  topProducts: null,
  query: '',
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

type ComponentState = {
  topSearches: SearchHistorySuggestion[]
  topProducts: SearchHit[]
}

export default withShopAndRuntimeConfig(PreQuerySuggestions)
